import React, {ReactElement} from "react";
import {BaseApplet, BaseAppletProps, BaseAppletState} from "../BaseApplet";
import {$KTS, Action, ACTION_SPACE, KeyTextStrings, ProfilePhoto} from "../../../shared/types";
import {AddOutlined} from "@mui/icons-material";
import {EditCaseHelper} from "./EditCaseHelper";
import {PageFragment, PageFragmentProps, PageFragmentState, PageFragmentStyleFlags} from "../../../shared/PageFragment";
import {Case, CaseFile, Cases} from "../../../zyrcona-shared/types";
import {TableDataContainer, TableDataRow} from "../../../shared/TableDataContainer";
import {TableData} from "../../../shared/tabledata";
import {TableDataSourceFragmentProps, TableDataSourceFragmentState} from "../../../shared/TableDataSourceFragment";
import {ListTableDataSourceFragment} from "../../../shared/ListTableDataSourceFragment";
import {DateUtil, FORMAT_SHOW_YEAR} from "../../../shared/date_util";
import {AppletTabsContainer} from "../AppletTabsContainer";
import {Typography} from "@mui/material";
import {GalleryUpload, GalleryUploadChange, GalleryUploadFile} from "../../../zyrcona-shared/components/GalleryUpload";
import {MimeTypeMap} from "../../../shared/mime_types";
import {BaseApp} from "../../../shared/BaseApp";
import {GalleryHelper} from "../../../zyrcona-shared/components/GalleryHelper";

export type CasesAppletProps = BaseAppletProps & {}

type CasesAppletState = BaseAppletState & {
  caseId?: string,
}

export class CasesApplet extends BaseApplet<CasesAppletProps, CasesAppletState> {

  protected getTitle(): string | null | undefined {
    return "Cases";
  }

  protected getToolbar(toolbarModeId: string | null): React.ReactElement {
    return this.renderToolbarButtonsInToolbarContainerEx([
      ACTION_SPACE,
      new Action("New Case", () => EditCaseHelper.addCase(), AddOutlined).setVariant("contained"),
    ]);
  }

  protected renderContainerContent(): React.ReactElement | null {
    if (this.state.caseId) {
      return <CaseFragment caseId={this.state.caseId} onBack={() => this.setState({caseId: null})}/>;
    }
    return super.renderContainerContent();
  }

  protected renderApplet(): React.ReactElement {
    return <CasesFragment path={this.props.path} onSelected={caseId => this.setState({caseId})}/>;
  }
}

class CasesTableDataRow implements TableDataRow {

  @TableData({name: "Title"})
  title: string;

  @TableData({name: "", type: "profile_photo"})
  profilePhoto: string;

  @TableData({name: "Patient"})
  patientName: string;

  @TableData({name: "Created at"})
  createdAt: string;
}

export type CasesFragmentProps = TableDataSourceFragmentProps & {
  onSelected: (caseId: string) => void,
}

type CasesFragmentState = TableDataSourceFragmentState & {}

export class CasesFragment extends ListTableDataSourceFragment<Case, CasesTableDataRow, Cases, CasesFragmentProps, CasesFragmentState> {

  constructor(props: CasesFragmentProps, context: any) {
    super(props, context, Cases.getInstance());
  }

  createTableDataRow(): CasesTableDataRow {
    return new CasesTableDataRow();
  }

  applyTableDataToRow(data: Case, content: CasesTableDataRow): void {
    content.title = data.title;
    content.profilePhoto = ProfilePhoto(data.patient?.basicInfo.profilePhoto);
    content.patientName = data.patient?.displayName();
    content.createdAt = DateUtil.formatDateTime(data.created, FORMAT_SHOW_YEAR);
  }

  onTableDataSelected(data: Case) {
    this.props.onSelected(data.id);
  }

  onTableCellChanged(data: Case, cellId: string, value: any): void {
  }

  renderContent(): ReactElement {
    const cases = this.loader.getListItems();
    return <TableDataContainer
      path={this.props.path}
      data={(from, to) => cases.slice(from, to)}
      dataTotal={cases.length}
      source={this}
      multiSelectListener={this}/>;
  }
}

type CaseFragmentProps = PageFragmentProps & {
  caseId: string,
  onBack: () => void,
}

enum CaseTab {
  FILES = "files",
}

const CASE_TAB_KTS = new KeyTextStrings([
  $KTS(CaseTab.FILES, "Files"),
]);

type CaseFragmentState = PageFragmentState & {
  case?: Case,
  caseTab: CaseTab,
}

class CaseFragment extends PageFragment<CaseFragmentProps, CaseFragmentState> {

  protected onCreateState(props?: CaseFragmentProps): CaseFragmentState {
    return {
      ...super.onCreateState(props),
      caseTab: CaseTab.FILES,
    };
  }

  protected styleFlags(): number | PageFragmentStyleFlags {
    return PageFragment.STYLE_TOOLBAR_TYPE_FLAG | CaseFragment.STYLE_BACK_BUTTON_FLAG;
  }

  onBackButtonClicked() {
    this.props.onBack();
  }

  protected async fetchOnMount(forceReload?: boolean): Promise<any> {
    const case_ = await Cases.getInstance().getOrLoadItem(this.props.caseId);
    this.setState({
      case: case_,
      title: case_?.title,
    });
  }

  renderContent(): React.ReactElement {
    if (!this.state.case) {
      return <Typography>Case not found.</Typography>
    }
    return <AppletTabsContainer kts={CASE_TAB_KTS} onSelect={key => this.setState({caseTab: key as CaseTab})}>
      {this.renderCaseTab()}
    </AppletTabsContainer>
  }

  private renderCaseTab(): ReactElement {
    switch (this.state.caseTab) {
      case CaseTab.FILES:
        const case_ = this.state.case;
        return <GalleryUpload
          galleryId={case_.id}
          mimeTypes={[".stl", ".ply"]}
          storedDataSourceBasePath={"case-files"}
          value={case_.files.map(file => ({url: file.url, mimeType: file.mimeType}))}
          setValue={(value: GalleryUploadFile[], change: GalleryUploadChange) => {
            const index = change?.index;
            if (!Number.isInteger(index) || index < 0) {
              return;
            }
            const uploadFile = value[index];
            if (!uploadFile) {
              case_.files.splice(index, 1);
            } else {
              const name = change.file?.file?.name || "File";
              const file = new CaseFile(uploadFile.url, Date.now(), uploadFile.mimeType || MimeTypeMap.MimeType_application_zip, change.file?.length || 0, name);
              if (index >= case_.files.length) {
                case_.files.push(file);
              } else {
                case_.files.splice(index, 1, file);
              }
            }
            Cases.getInstance().addListItem(case_)
              .then(() => BaseApp.CONTEXT.showToast("Uploaded!"));
          }}
          showGalleryDialog={GalleryHelper.showGalleryDialog}
        />;
    }
    return null;
  }
}